/**
 * @module core/dom
 *
 * Small DOM helpers used across the app. Deliberately dependency-free so the
 * modules stay testable under jsdom and easy to reason about.
 */

/**
 * Query a single element.
 *
 * @template {Element} [T=Element]
 * @param {string} selector
 * @param {ParentNode} [scope=document]
 * @returns {T|null}
 */
export function qs(selector, scope = document) {
  return /** @type {T|null} */ (scope.querySelector(selector));
}

/**
 * Query all elements as a real array.
 *
 * @template {Element} [T=Element]
 * @param {string} selector
 * @param {ParentNode} [scope=document]
 * @returns {T[]}
 */
export function qsa(selector, scope = document) {
  return /** @type {T[]} */ (Array.from(scope.querySelectorAll(selector)));
}

/**
 * Create an element from an HTML string.
 *
 * @param {string} html Markup with a single root node.
 * @returns {Element} The first element child.
 */
export function el(html) {
  const template = document.createElement("template");
  template.innerHTML = html.trim();
  const node = template.content.firstElementChild;
  if (!node) {
    throw new Error("core/dom: el() received markup without an element child");
  }
  return node;
}

/**
 * Add an event listener and return a disposer.
 *
 * @param {EventTarget} target
 * @param {string} type
 * @param {EventListenerOrEventListenerObject} handler
 * @param {AddEventListenerOptions|boolean} [options]
 * @returns {() => void} Call to remove the listener.
 */
export function on(target, type, handler, options) {
  target.addEventListener(type, handler, options);
  return () => target.removeEventListener(type, handler, options);
}

/**
 * Replace the children of a node with new markup.
 *
 * @param {Element} node
 * @param {string} html
 * @returns {void}
 */
export function setHTML(node, html) {
  node.innerHTML = html;
}

/**
 * Toggle a set of boolean attributes based on a state map.
 *
 * @param {Element} node
 * @param {Record<string, boolean>} attributes e.g. `{ "aria-selected": true }`
 * @returns {void}
 */
export function setAttributes(node, attributes) {
  for (const [name, value] of Object.entries(attributes)) {
    if (value === false || value === null || value === undefined) {
      node.removeAttribute(name);
    } else {
      node.setAttribute(name, value === true ? "" : String(value));
    }
  }
}

/**
 * Trailing debounce.
 *
 * @template {(...args: any[]) => void} F
 * @param {F} fn
 * @param {number} [wait=150]
 * @returns {(...args: Parameters<F>) => void}
 */
export function debounce(fn, wait = 150) {
  /** @type {ReturnType<typeof setTimeout>|undefined} */
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/**
 * Throttle a callback to one call per animation frame.
 *
 * @template {(...args: any[]) => void} F
 * @param {F} fn
 * @returns {(...args: Parameters<F>) => void}
 */
export function rafThrottle(fn) {
  /** @type {number|null} */
  let frame = null;
  /** @type {any[]} */
  let lastArgs = [];
  return (...args) => {
    lastArgs = args;
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      fn(...lastArgs);
    });
  };
}

/**
 * Clamp a number between a minimum and maximum.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Linear interpolation.
 *
 * @param {number} from
 * @param {number} to
 * @param {number} progress 0–1
 * @returns {number}
 */
export function lerp(from, to, progress) {
  return from + (to - from) * progress;
}
